import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
//  Copyright   : 2020 semantics GmbH
//  Date        : $Date: 2026-08-18 16:13:09 +0200 (Tue, 18 Aug 2026) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 164873 $
//  Purpose     : custom column definitions for fulltext package
// =============================================================================
// ---Third-party imports
import React from 'react';
// ---Framework imports
import { dict } from 'ldash';
import { registerColumnTypes, ColumnTypeNames as $ } from 'uix/db/columnTypes';
import { filterMatchSorter, textFilter } from 'uix/components/table/cols';
import { useEffect } from 'uix/hooks';
import log from 'uix/logging';
// ---Local imports
import { useOcrConfig } from './hooks';
// ---Globals
export const getOcrConfigLabel = (coid, ocr_cfg)=>{
    if (ocr_cfg?.[coid]) {
        let { language, textType } = ocr_cfg[coid];
        return `${language.join(', ')} / ${textType.join(', ')}`;
    }
    return coid === null ? null : `${coid}`;
};
const OcrConfig = ({ coid })=>{
    let ocr_cfg = useOcrConfig();
    if (!ocr_cfg) {
        return null;
    }
    return getOcrConfigLabel(coid, ocr_cfg);
};
const OcrConfigTextFilter = textFilter({
    name: 'ocr-config'
});
const OcrConfigFilter = ({ filter, onChange })=>{
    let ocr_cfg = useOcrConfig();
    let applyFilter = (text)=>onChange({
            text,
            transform: (coid)=>getOcrConfigLabel(coid, ocr_cfg)
        });
    useEffect(()=>{
        if (filter?.value?.text) {
            applyFilter(filter.value.text);
        }
    }, [
        ocr_cfg
    ]);
    return /*#__PURE__*/ _jsx(OcrConfigTextFilter, {
        filter: filter,
        onChange: applyFilter
    });
};
const OcrConfigColumn = (env, colid, options)=>{
    let filter = filterMatchSorter(colid);
    return {
        ...filter,
        Filter: OcrConfigFilter,
        Cell: ({ value })=>value ? /*#__PURE__*/ _jsx(OcrConfig, {
                coid: value
            }) : null,
        aggregate: (vals)=>'',
        ...options
    };
};
registerColumnTypes({
    OcrConfigColumn
});
export const columns = [
    // OCR - Config
    [
        $.OcrConfigColumn,
        'co.id'
    ],
    // OCR - Pages
    /*
  [
    $.TextColumn,
    'oo.type',
    {
      i18nKey: 'OCR Typ',
      groupBySupport: true,
      translate: v =>
        v > 0 ? {1: 'FineReader', 2: 'FineReader (man)', 3: 'FineReader (imp)'}[v] : null,
    },
  ],

  [$.PercentageColumn, 'oo.charconfidence', {i18nKey: 'OCR Qualität (C)'}],
  [$.PercentageColumn, 'oo.wordpenalty', {i18nKey: 'OCR Qualität (W)', inverted: true}],

  [
    $.BooleanColumn,
    'oo.checklanguage',

    {
      i18nKey: 'OCR Sprache prüfen',
      trueVal: 1,
      icon: 'exclamation triangle',
    },
  ],
  [
    $.BooleanColumn,
    'oo.checkresolution',
    {
      i18nKey: 'OCR Auflösung prüfen',
      trueVal: 1,
      icon: 'exclamation triangle',
    },
  ],

  [$.NumberColumn, 'oo.wordcount', {i18nKey: 'OCR Wörter'}],
  [$.NumberColumn, 'oo.licencecount', {i18nKey: 'OCR Antiqua'}],
  [$.NumberColumn, 'oo.licencegothic', {i18nKey: 'OCR Fraktur'}],
  [
    $.NumberColumn,
    'oo.skewangle',
    {
      i18nKey: 'OCR Drehung',
      transform: v => (v ? (v * (180 / Math.PI)).toFixed(1) : undefined),
    },
  ],

  [$.DateColumn, 'oo.insertts', {i18nKey: 'OCR Datum'}],
  */ [
        $.CustomBooleanColumn,
        'oor.exists',
        {
            fields: [
                'oor.id'
            ]
        }
    ],
    [
        $.PercentageColumn,
        'oor.correctionratio',
        {
            inverted: true,
            factor: 100
        }
    ],
    [
        $.PercentageColumn,
        'oor.correctionratiobagged',
        {
            inverted: true,
            factor: 100
        }
    ],
    [
        $.PercentageColumn,
        'oor.charconf'
    ],
    ...[
        'word',
        'line',
        'para',
        'block'
    ].map((n)=>[
            $.NumberColumn,
            `oor.${n}count`,
            {}
        ])
];
